<template>
	<v-row class="full-height">
		<v-col cols="12" class="flex-none">
			<div class="color-actions">
				<v-btn
					depressed
					outlined
					@click="duplicatePalette()"
					:disabled="duplicateActive || paletteData.colorPaletteId == null"
					v-if="canManageColorPalettes"
					>Duplicate</v-btn
				>
				<v-btn
					depressed
					outlined
					@click="deleteWarning = !deleteWarning"
					:disabled="paletteData.colorPaletteId == null"
					v-if="canManageColorPalettes && !hasAssociatedStyleCodes"
					>Delete</v-btn
				>
				<v-tooltip bottom v-else>
					<template v-slot:activator="{ on, attrs }">
						<span v-on="on" v-bind="attrs">
							<v-btn
								depressed
								outlined
								@click="deleteWarning = !deleteWarning"
								:disabled="paletteData.colorPaletteId == null || hasAssociatedStyleCodes"
								v-if="canManageColorPalettes"
								>Delete</v-btn
							>
						</span>
					</template>
					<span style="white-space: pre-line">{{ deleteTooltipMessage }}</span>
				</v-tooltip>
				<v-btn depressed outlined @click="cancelAndClearState">Cancel</v-btn>
				<v-btn
					v-if="canManageColorPalettes"
					depressed
					color="success"
					:loading="saving"
					:disabled="saving"
					@click="shouldShowDialog ? (confirmDialog = true) : saveColorPalette()"
					>Save</v-btn
				>
			</div>
		</v-col>

		<v-col cols="6" class="flex-auto mx-auto">
			<div class="color-form">
				<div class="form-input">
					<label>Color Palette Name</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="paletteData.name"
					></v-text-field>
				</div>

				<div class="form-input">
					<label>Description</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="paletteData.description"
					></v-text-field>
				</div>
			</div>

			<div class="color-table">
				<div class="color-table-description">
					<p><em>Deleting a color will NOT affect any past orders</em></p>
				</div>
				<div class="color-table-container">
					<div class="flex-table" role="table-head">
						<div class="flex-table-row" role="row-group">
							<div class="flex-table-cell" role="column-header">&nbsp;</div>
							<div class="flex-table-cell" role="column-header">Color</div>
							<div class="flex-table-cell" role="column-header">Description</div>
							<div class="flex-table-cell" role="column-header">Hex Code</div>
							<div class="flex-table-cell" role="column-header">&nbsp;</div>
							<div class="flex-table-cell" role="column-header">Custom</div>
							<div class="flex-table-cell" role="column-header">&nbsp;</div>
						</div>
					</div>
					<div class="flex-table" role="table-body">
						<div
							v-for="(data, i) in paletteData.colors"
							:key="i"
							class="flex-table-row"
							role="row-group"
						>
							<div class="flex-table-cell" role="cell">{{ i + 1 }}</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="data.label"
										:disabled="!canManageColorPalettes"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="data.description"
										:disabled="!canManageColorPalettes"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										prepend-inner-icon="mdi-pound"
										outlined
										dense
										hide-details="auto"
										v-model="data.hexCode"
										:disabled="!canManageColorPalettes"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<ColorDisplay :hex-code="data.hexCode" :is-custom-color="data.isCustomColor" shape="circle" size="medium" />
							</div>
							<div class="flex-table-cell" role="cell">
								<v-checkbox
									v-model="data.isCustomColor"
									:disabled="!canManageColorPalettes"
									hide-details
									dense
									class="custom-color-checkbox"
								/>
							</div>
							<div class="flex-table-cell" role="cell">
								<v-btn
									:disabled="!canManageColorPalettes"
									icon
									color="primary"
									@click="removeColorRow(data)"
									><v-icon style="font-size: 2rem">mdi-delete</v-icon>
								</v-btn>
							</div>
						</div>
					</div>
				</div>
				<div class="color-table-actions">
					<v-btn v-if="canManageColorPalettes" text @click="addColorRow()"
						><v-icon>mdi-plus</v-icon> Add a Row</v-btn
					>
				</div>
			</div>
		</v-col>

		<WarningModal
			v-if="deleteWarning"
			:showModal="deleteWarning"
			:message="'Are you sure you want to delete this color palette?'"
			:cancelText="'No, Cancel'"
			:confirmText="'Yes, Delete'"
			@cancel="deleteWarning = false"
			@confirm="
				deleteColorPalette();
				deleteWarning = false;
			"
		/>

		<v-dialog v-if="confirmDialog" v-model="confirmDialog" persistent width="1200">
			<v-card class="d-flex flex-column" style="max-height: 80vh">
				<v-card-title class="text-h5 mb-2 flex-shrink-0">
					Select the style codes in which you would like new colors to be enabled.
				</v-card-title>
				<v-card-text style="overflow-y: auto; flex: 1 1 auto">
					<div class="color-table-dialog">
						<div class="flex-table" role="table-head">
							<div class="flex-table-row" role="row-group">
								<div class="flex-table-cell" role="column-header">
									<v-checkbox v-model="selectAll" @change="toggleSelectAll" />
								</div>
								<div class="flex-table-cell" role="column-header">&nbsp;</div>
								<div class="flex-table-cell" role="column-header">Style Code</div>
								<div class="flex-table-cell" role="column-header">Description</div>
							</div>
						</div>
						<div class="flex-table" role="table-body">
							<div
								v-for="(data, i) in associatedColorPaletteItems"
								:key="i"
								class="flex-table-row"
								role="row-group"
							>
								<div class="flex-table-cell" role="cell">
									<v-checkbox v-model="data.selected" />
								</div>
								<div class="flex-table-cell" role="cell">
									<SystemCategoryIcon :system-category-id="data.systemCategoryId" />
								</div>
								<div class="flex-table-cell" role="cell">
									<div class="form-input">
										<span>{{ data.styleCode }}</span>
									</div>
								</div>
								<div class="flex-table-cell" role="cell">
									<div class="form-input">
										<span>{{ data.itemDescription }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</v-card-text>
				<v-card-actions class="flex-shrink-0">
					<v-spacer></v-spacer>
					<v-btn
						text
						@click="
							() => {
								this.confirmDialog = false;
							}
						"
					>
						Cancel
					</v-btn>
					<v-btn
						v-if="canManageColorPalettes"
						color="green darken-1"
						text
						@click="
							() => {
								this.confirmDialog = false;
								this.saveColorPalette();
							}
						"
					>
						Save
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import WarningModal from "@/components/WarningModal.vue";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import ColorDisplay from "@/components/ColorDisplay.vue";
import { hasPermission, SystemPermissions } from "@/models/User";
import { getApiUrl } from "@/helpers";

export default {
	name: "PaletteView",
	props: {
		colorPaletteId: {
			type: Number,
		},
	},
	components: {
		WarningModal,
		SystemCategoryIcon,
		ColorDisplay,
	},
	data() {
		return {
			paletteData: {
				name: "",
				description: "",
				colors: [
					{
						label: "",
						description: "",
						hexCode: "",
						isCustomColor: false,
					},
				],
			},
			duplicateActive: false,
			deleteWarning: false,
			originalPaletteData: {},
			shouldShowDialog: false,
			confirmDialog: false,
			selectAll: true,
			saving: false,
		};
	},
	apollo: {
		associatedColorPaletteItems: {
			query() {
				return gql`
					query GetAssociatedColorPaletteItems($templateColorPaletteId: Int!) {
						associatedColorPaletteItems(templateColorPaletteId: $templateColorPaletteId) {
							colorPaletteIds
							templateColorPaletteId
							name
							description
							isTemplate
							inventoryItemId
							styleCode
							systemCategoryId
							itemDescription
							associatedStyleCodes
						}
					}
				`;
			},
			variables() {
				return {
					templateColorPaletteId: this.paletteData.colorPaletteId,
				};
			},
			skip() {
				return !this.paletteData.colorPaletteId;
			},
			update: (data) => {
				data.associatedColorPaletteItems.forEach((item) => {
					item.selected = true;
				});
				return data.associatedColorPaletteItems;
			},
		},
	},
	methods: {
		addColorRow() {
			let newColor = {
				label: "",
				description: "",
				hexCode: "",
				isCustomColor: false,
			};

			this.paletteData.colors.push(newColor);
		},

		removeColorRow(rowData) {
			//filter out the passed in rowData
			this.paletteData.colors = this.paletteData.colors.filter((obj) => obj !== rowData);
		},

		duplicatePalette() {
			//remove the palette id so we can save a new copy and replace the name
			this.paletteData.colorPaletteId = null;
			this.paletteData.name = `${this.paletteData.name} (DUPLICATE)`;

			//set duplcate state
			this.duplicateActive = true;
		},

		async deleteColorPalette() {
			try {
				const res = await this.$http.delete(
					getApiUrl(`/api/settings/color-palette/${this.paletteData.colorPaletteId}`)
				);
				console.log(res);

				this.$router.push("/inventory/color-palette");
			} catch (error) {
				console.log(error);
			}
		},

		async saveColorPalette() {
			this.saving = true;
			try {
				let res;
				if (this.paletteData.colorPaletteId) {
					let payload = {
						...this.paletteData,
						associations: this.associatedColorPaletteItems || [],
					};

					res = await this.$http.put(
						getApiUrl("/api/settings/color-palette"),
						payload
					);
				} else {
					const palette = {
						...this.paletteData,
						colors: this.paletteData.colors.map((obj) => {
							const colorObj = {
								description: obj.description,
								hexCode: obj.hexCode,
								label: obj.label,
								colorPaletteId: obj.colorPaletteId,
								isCustomColor: obj.isCustomColor || false,
							};
							return colorObj;
						}),
					};
					res = await this.$http.post(
						getApiUrl("/api/settings/color-palette"),
						palette
					);
				}
				console.log(res);

				this.$router.push("/inventory/color-palette");
			} catch (error) {
				console.log(error);
			} finally {
				this.saving = false;
			}
		},

		isEqual(obj1, obj2) {
			return JSON.stringify(obj1) === JSON.stringify(obj2);
		},

		toggleSelectAll() {
			this.associatedColorPaletteItems.forEach((item) => {
				item.selected = this.selectAll;
			});
		},

		cancelAndClearState() {
			// Clear the saved state for color palette list to prevent auto-navigation issues
			this.$store.dispatch("globalState/saveColorPaletteState", {
				offset: 0,
				keywords: "",
				scrollPosition: 0,
			});

			// Navigate back to list
			this.$router.push("/inventory/color-palette");
		},

		async getColorPalette() {
			try {
				const res = await this.$apollo.query({
					query: gql`
						query GetColorPalette($colorPaletteId: Int!) {
							colorPalettes(
								where: { colorPaletteId: { eq: $colorPaletteId }, isTemplate: { eq: true } }
							) {
								items {
									colorPaletteId
									name
									description
									upcharge
									isTemplate
									colors {
										colorId
										colorPaletteId
										description
										hexCode
										label
										isVisible
										isRetired
										isCustomColor
									}
								}
							}
						}
					`,
					variables: {
						colorPaletteId: this.colorPaletteId,
					},
					fetchPolicy: "no-cache",
				});

				// Since we're querying by ID, we expect only one item
				const palette = res.data.colorPalettes.items[0];
				if (palette) {
					this.paletteData = palette;
					// Keep a copy of the original data for comparison
					this.originalPaletteData = JSON.parse(JSON.stringify(palette));
				}
			} catch (error) {
				console.error("Error fetching color palette:", error);
			}
		},
	},
	watch: {
		paletteData: {
			deep: true, // Watch nested properties of paletteData
			handler(newPaletteData, oldPaletteData) {
				// Check if chartData has changed
				const hasNewItems = newPaletteData.colors.some((item) => item.colorPaletteId === undefined);
				if (
					hasNewItems &&
					this.associatedColorPaletteItems &&
					this.associatedColorPaletteItems.length > 0
				) {
					this.shouldShowDialog = true;
				} else {
					this.shouldShowDialog = false;
				}
			},
		},
		associatedColorPaletteItems: {
			deep: true,
			handler(newItems, oldItems) {
				this.selectAll = this.allItemsSelected;
			},
		},
	},
	computed: {
		canManageColorPalettes() {
			const user = this.$store.getters.userInfo;
			return (
				hasPermission(user, SystemPermissions.Inventory_ColorPalette_Create_Update_Delete) ||
				user.isAdmin
			);
		},
		allItemsSelected() {
			return this.associatedColorPaletteItems.every((item) => item.selected);
		},
		hasAssociatedStyleCodes() {
			return this.associatedColorPaletteItems && this.associatedColorPaletteItems.length > 0;
		},
		deleteTooltipMessage() {
			if (!this.hasAssociatedStyleCodes) return "";

			const styleCodesText = this.associatedColorPaletteItems
				.map((item) => item.styleCode)
				.join("\n");

			return `Cannot delete color palette - it is associated with the following style codes:\n${styleCodesText}`;
		},
	},
	mounted() {
		if (this.colorPaletteId) {
			this.getColorPalette();
		}
	},
};
</script>

<style lang="scss" scoped>
.color {
	&-actions {
		display: flex;
		justify-content: flex-end;

		.v-btn {
			background-color: $color-white;
			border-color: $color-gray;
			margin-left: 0.5rem;
		}
	}

	&-table {
		display: flex;
		flex-flow: column nowrap;
		height: 100%;
		margin-top: 2rem;

		&-description {
			p {
				color: $color-gray;
				font-size: 1.4rem;
			}
		}

		&-container {
			position: relative;
			height: 0; //create scrollable content space since contianer will grow automatically based on content
			overflow-y: auto;
			flex-grow: 1;
			background-color: $color-white;

			.flex-table[role="table-head"] {
				position: sticky;
				top: 0;
				z-index: 1;
			}

			.flex-table-cell {
				.form-input {
					width: 100%;

					&::v-deep .v-icon {
						font-size: 1.8rem;
					}
				}

				&:nth-child(1) {
					width: 5%;
					justify-content: center;
				}
				&:nth-child(2) {
					width: 22%;
				}
				&:nth-child(3) {
					width: 22%;
				}
				&:nth-child(4) {
					width: 22%;
				}
				&:nth-child(5) {
					width: 8%;
					justify-content: center;
				}
				&:nth-child(6) {
					width: 13%;
					justify-content: center;
				}
				&:nth-child(7) {
					width: 8%;
					justify-content: center;
				}
			}
		}

		&-actions {
			margin-top: 1rem;

			.v-btn {
				font-size: 1.4rem;
				padding-left: 1rem;

				.v-icon {
					font-size: 2rem;
					margin-right: 0.5rem;
				}
			}
		}
	}

	&-table-dialog {
		.flex-table-cell {
			.form-input {
				width: 100%;
			}

			&:nth-child(1) {
				width: 8%;
				justify-content: center;
			}
			&:nth-child(2) {
				width: 8%;
				justify-content: center;
			}
			&:nth-child(3) {
				width: 40%;
			}
			&:nth-child(4) {
				width: 40%;
			}
			&:nth-child(5) {
				width: 4%;
				justify-content: center;
			}
		}
	}

	&-preview {
		display: block;
		width: 3rem;
		height: 3rem;
		border: 1px solid $color-gray-light-2;
		border-radius: 25px;
	}
}
</style>
